import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { ChevronDown } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import dentalImplantsImg from "@/assets/treatments/dental-implants.jpg";
import allOn4Img from "@/assets/treatments/all-on-4.jpg";
import fullMouthRestorationImg from "@/assets/treatments/full-mouth-restoration.jpg";
import veneersImg from "@/assets/treatments/veneers.jpg";
import crownsImg from "@/assets/treatments/crowns.jpg";
import rootCanalImg from "@/assets/treatments/root-canal.jpg";

const Treatments = () => {
  const { t } = useTranslation();

  const handleScheduleClick = () => {
    const contactSection = document.getElementById('contact');
    if (contactSection) {
      contactSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const treatments = [
    {
      title: t('treatments.dentalImplants.title'),
      description: t('treatments.dentalImplants.description'),
      link: '/treatments/dental-implants',
      image: dentalImplantsImg,
    },
    {
      title: t('treatments.allOn4.title'),
      description: t('treatments.allOn4.description'),
      link: '/treatments/all-on-4',
      image: allOn4Img,
      objectPosition: 'object-center',
    },
    {
      title: t('treatments.fullMouthRestoration.title'),
      description: t('treatments.fullMouthRestoration.description'),
      link: '/treatments/full-mouth-restoration',
      image: fullMouthRestorationImg,
      objectPosition: 'object-center',
    },
    {
      title: t('treatments.veneers.title'),
      description: t('treatments.veneers.description'),
      link: '/treatments/veneers',
      image: veneersImg,
      objectPosition: 'object-center',
    },
    {
      title: t('treatments.crowns.title'),
      description: t('treatments.crowns.description'),
      link: '/treatments/crowns',
      image: crownsImg,
      objectPosition: 'object-center',
    },
    {
      title: t('treatments.rootCanal.title'),
      description: t('treatments.rootCanal.description'),
      link: '/treatments/root-canal',
      image: rootCanalImg,
    },
  ];

  return (
    <section id="treatments" className="py-16 sm:py-20 md:py-24 bg-background">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Header */}
        <header className="text-center mb-12 sm:mb-14 md:mb-16 space-y-3 sm:space-y-4">
          <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight px-2">
            {t('treatments.title')}
          </h2>
          <div className="w-16 sm:w-20 h-1 bg-primary mx-auto"></div>
          <p className="text-base sm:text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto leading-relaxed px-4">
            {t('treatments.subtitle')}
          </p>
        </header>

        {/* Treatments Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8 lg:gap-10">
          {treatments.map((treatment, index) => (
            <article
              key={index}
              className="group cursor-pointer"
            >
              <Card className="overflow-hidden border-border/50 hover:border-primary/50 transition-all duration-500 h-full">
                {/* Treatment Image */}
                <div className="relative aspect-[4/3] overflow-hidden bg-muted">
                  <img
                    src={treatment.image}
                    alt={treatment.title}
                    className={`w-full h-full object-cover group-hover:scale-110 transition-transform duration-500 ${treatment.objectPosition || ''}`}
                    loading="lazy"
                    width={400}
                    height={300}
                    decoding="async"
                  />
                </div>

                {/* Content */}
                <CardContent className="p-4 sm:p-6 lg:p-8 space-y-3 sm:space-y-4">
                  <h3 className="text-lg sm:text-xl lg:text-2xl font-bold tracking-tight group-hover:text-primary transition-colors duration-300">
                    {treatment.title}
                  </h3>
                  <p className="text-muted-foreground leading-relaxed text-sm sm:text-base">
                    {treatment.description}
                  </p>
                  {treatment.link ? (
                    <Link to={treatment.link}>
                      <Button
                        variant="ghost"
                        className="p-0 h-auto font-semibold text-primary hover:text-primary/80 hover:bg-transparent text-sm sm:text-base"
                      >
                        {t('treatments.learnMore')} →
                      </Button>
                    </Link>
                  ) : (
                    <Button
                      variant="ghost"
                      className="p-0 h-auto font-semibold text-primary hover:text-primary/80 hover:bg-transparent text-sm sm:text-base"
                      onClick={handleScheduleClick}
                    >
                      {t('treatments.cta')} →
                    </Button>
                  )}
                </CardContent>
              </Card>
            </article>
          ))}
        </div>

        {/* CTA Section */}
        <div className="text-center mt-12 sm:mt-16 lg:mt-20 flex flex-col sm:flex-row items-center justify-center gap-4">
          <Button
            size="lg"
            className="px-6 sm:px-8 lg:px-12"
            onClick={handleScheduleClick}
          >
            {t('treatments.cta')}
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="lg" className="px-6 sm:px-8 lg:px-12 gap-2 group">
                {t('treatments.moreTreatments')}
                <ChevronDown className="h-4 w-4 transition-transform duration-200 group-data-[state=open]:rotate-180" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="center"
              className="bg-background/95 backdrop-blur-md border-border shadow-xl z-[100] min-w-[220px]"
            >
              <DropdownMenuItem asChild>
                <Link to="/treatments/all-on-x" className="text-sm font-medium text-foreground hover:text-primary cursor-pointer w-full">
                  {t('treatments.moreTreatmentsItems.allOnX')}
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to="/treatments/smile-design" className="text-sm font-medium text-foreground hover:text-primary cursor-pointer w-full">
                  {t('treatments.moreTreatmentsItems.smileDesign')}
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to="/implant-supported-bridge" className="text-sm font-medium text-foreground hover:text-primary cursor-pointer w-full">
                  {t('treatments.moreTreatmentsItems.implantBridge')}
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to="/treatments/teeth-whitening" className="text-sm font-medium text-foreground hover:text-primary cursor-pointer w-full">
                  {t('treatments.moreTreatmentsItems.teethWhitening')}
                </Link>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </section>
  );
};

export default Treatments;
